import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BulkButtons } from "@/components/common/BulkUpload";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_CONFIG = {
  "finish-goods":  { stock: true, labelKey: "finish_goods" },
  "semi-finished-goods": { stock: true, labelKey: "semi_finished_goods" },
  "cartons":        { stock: true, labelKey: "cartons" },
  "plastics":       { stock: true, labelKey: "plastics" },
  "other-items":    { stock: true, labelKey: "other_items" },
  "buyers":         { stock: false, labelKey: "buyers" },
  "products":       { stock: false, labelKey: "products" },
  "brands":         { stock: false, labelKey: "brands" },
  "packing-styles": { stock: false, labelKey: "packing_styles" },
  "vendors":        { stock: false, vendor: true, labelKey: "vendors" },
};

function MasterTable({ kind }) {
  const { t } = useLang();
  const { can } = useAuth();
  const canEdit = can("masters", "edit");
  const cfg = KIND_CONFIG[kind];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/masters/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind]);

  const openNew = () => { setEditing(null); setForm({ code: "", name: "", description: "", unit: "pcs", stock: 0, lead_time_days: 7, vendor: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/masters/${kind}/${editing.id}`, form);
      else await api.post(`/masters/${kind}`, form);
      toast.success(t("save"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/masters/${kind}/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const columns = [
    { key: "code", label: t("code"), className: "font-mono text-xs" },
    { key: "name", label: t("name"), className: "font-medium" },
    ...(cfg.stock ? [
      { key: "stock", label: t("stock"), align: "right", render: (it) => (
        <div className="font-mono" data-testid={`${kind}-stock-${it.code}`}>
          <div className="font-semibold">{fmtQty(it.stock)}</div>
          <div className="text-[10px] text-slate-400 whitespace-nowrap">C {fmtQty(it.stock_by_wh?.central)} · S {fmtQty(it.stock_by_wh?.stamp)} · P {fmtQty(it.stock_by_wh?.printing)} · FG {fmtQty(it.stock_by_wh?.fg_wh)} · SFG {fmtQty(it.stock_by_wh?.sfg_wh)}</div>
        </div>
      ) },
      { key: "reject", label: t("reject_col"), align: "right", value: (it) => it.stock_by_wh?.reject || 0,
        render: (it) => <span className="font-mono text-rose-700" data-testid={`${kind}-reject-${it.code}`}>{fmtQty(it.stock_by_wh?.reject)}</span> },
      { key: "unit", label: t("unit") },
      { key: "lead_time_days", label: t("lead_time"), align: "right" },
      { key: "vendor", label: t("vendor"), className: "text-slate-600" },
    ] : cfg.vendor ? [
      { key: "phone", label: t("phone"), className: "font-mono text-xs whitespace-nowrap" },
      { key: "address", label: t("address"), className: "text-slate-600" },
    ] : [{ key: "description", label: t("description"), className: "text-slate-600" }]),
    { key: "actions", label: "", filter: false, sort: false, render: (it) => canEdit && (
      <div className="flex gap-1 justify-end">
        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`${kind}-edit-${it.code}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-red-600 hover:text-red-700" data-testid={`${kind}-delete-${it.code}`}><Trash2 size={14} /></Button>
      </div>
    ) },
  ];

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t(cfg.labelKey)}
            </h3>
            <p className="text-xs text-slate-500">{items.length} records</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && <BulkButtons kind={kind} title={t(cfg.labelKey)} onDone={load} />}
            {canEdit && (
              <Button size="sm" onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid={`${kind}-add-btn`}>
                <Plus size={14} className="mr-2" />{t("add")}
              </Button>
            )}
          </div>
        </div>
        <DataTable columns={columns} rows={items} testid={`${kind}-table`} />
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${kind}-form-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? t("edit") : t("add")} - {t(cfg.labelKey)}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-wider">{t("code")}</Label>
              <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid={`${kind}-code-input`} />
            </div>
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-wider">{t("name")}</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${kind}-name-input`} />
            </div>
            {cfg.stock && <>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("unit")}</Label>
                <Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              {!editing && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("opening_stock")}</Label>
                  <Input type="number" value={form.stock ?? 0} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} data-testid={`${kind}-stock-input`} />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("lead_time")}</Label>
                <Input type="number" value={form.lead_time_days ?? 7} onChange={(e) => setForm({ ...form, lead_time_days: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("vendor")}</Label>
                <Input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
              </div>
            </>}
            {cfg.vendor ? <>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider">{t("phone")}</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`${kind}-phone-input`} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider">{t("address")}</Label>
                <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid={`${kind}-address-input`} />
              </div>
            </> : <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">{t("description")}</Label>
              <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid={`${kind}-save-btn`}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function MasterData() {
  const { t } = useLang();
  return (
    <div className="space-y-6" data-testid="master-data-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("master_data")}</h1>
        <p className="text-sm text-slate-500 mt-1">Finish Goods · Semi Finished Goods · Cartons · Plastics · Buyers · Products · Brands · Packing Styles · Vendors</p>
      </div>

      <Tabs defaultValue="finish-goods">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {Object.keys(KIND_CONFIG).map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              {t(KIND_CONFIG[k].labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(KIND_CONFIG).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <MasterTable kind={k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
